import { PromoLabelType } from "@omni/types/api/product/bff/shared/enums/promoLabelType.enum";
import { PromotionType } from "@omni/types/api/product/bff/shared/enums/promotionType.enum";
import { SalesIncentiveType } from "@omni/types/api/product/bff/shared/enums/salesIncentiveType.enum";
import { PromoLabel } from "@omni/types/api/product/bff/shared/promoLabel.model";
import { Promotion } from "@omni/types/api/product/bff/shared/promotion.model";
import { SalesIncentive } from "@omni/types/api/product/bff/shared/salesIncentive.model";
import { CloudPromotion } from "@omni/types/api/product/cloud/shared/promotion";
import { CloudSalesIncentive } from "@omni/types/api/product/cloud/shared/salesIncentive.model";
import { AlgoliaSearchPromotion } from "@omni/types/api/search/algolia/product/shared/promotion.model";
import { AlgoliaSearchSalesIncentive } from "@omni/types/api/search/algolia/product/shared/salesIncentive.model";
import { BffSearchPromotion } from "@omni/types/api/search/bff/product/shared/promotion.model";
import { BffSearchSalesIncentive } from "@omni/types/api/search/bff/product/shared/salesIncentive.model";
import { head } from "@omni/utils/array.utils";

import { PromotionMapper } from "@/product/shared/promotion.mapper";
import { SalesIncentiveMapper } from "@/product/shared/salesIncentive.mapper";

const getFormattedCashBackValue = (promotions?: Array<Promotion>) => {
    const cb = promotions?.find(
        (p) => p.promotionType === PromotionType.Cashback
    );

    if (cb?.promotionValue) {
        return `${cb.promotionValue}€`;
    }
    if (cb?.promotionPercent) {
        return `${cb.promotionPercent}%`;
    }
};

const getPromoLabel = (
    salesIncentives: Array<SalesIncentive>,
    discountPercentage: number | null,
    promotions: Array<Promotion>
): PromoLabel | undefined => {
    const highestPrioIncentive = head(
        salesIncentives.sort((a, b) => a.order - b.order)
    );

    if (!highestPrioIncentive) {
        return undefined;
    }

    const discountIncentives = [
        SalesIncentiveType.ActiePrijs,
        SalesIncentiveType.FolderPrijs,
        SalesIncentiveType.CombinatiePrijs,
        SalesIncentiveType.OutletPrijs,
        SalesIncentiveType.KortingProcent
    ];

    if (
        discountIncentives.some(
            (incentive) => incentive === highestPrioIncentive.type
        )
    ) {
        return {
            salesIncentiveType: highestPrioIncentive.type,
            promoLabelType: PromoLabelType.Promo,
            formattedPromotionValue: discountPercentage
                ? `${Math.floor(discountPercentage)}%`
                : undefined
        } satisfies PromoLabel;
    }

    switch (highestPrioIncentive.type) {
        case SalesIncentiveType.EveryDayLowPrice:
            return {
                salesIncentiveType: highestPrioIncentive.type,
                promoLabelType: PromoLabelType.EveryDayLowPrice
            } satisfies PromoLabel;
        case SalesIncentiveType.GratisTransport:
            return {
                salesIncentiveType: highestPrioIncentive.type,
                promoLabelType: PromoLabelType.FreeTransport
            } satisfies PromoLabel;
        case SalesIncentiveType.Cashback:
            return {
                salesIncentiveType: highestPrioIncentive.type,
                promoLabelType: PromoLabelType.Cashback,
                formattedPromotionValue: getFormattedCashBackValue(promotions)
            } satisfies PromoLabel;
        case SalesIncentiveType.GratisArtikel:
            return {
                salesIncentiveType: highestPrioIncentive.type,
                promoLabelType: PromoLabelType.FreeArticle
            } satisfies PromoLabel;
    }

    return undefined;
};

const fromCloudToBff = ({
    salesIncentives,
    discountPercentage,
    promotions
}: {
    salesIncentives: Array<CloudSalesIncentive> | undefined;
    discountPercentage: number | null;
    promotions: Array<CloudPromotion>;
}): PromoLabel | undefined => {
    const bffSalesIncentives = salesIncentives?.map(
        SalesIncentiveMapper.fromCloudToBff
    );

    const bffPromotions = promotions?.map(PromotionMapper.fromCloudToBff);

    return getPromoLabel(
        bffSalesIncentives ?? [],
        discountPercentage,
        bffPromotions
    );
};

const fromAlgoliaSearchToBff = ({
    salesIncentives,
    discountPercentage,
    promotions
}: {
    salesIncentives: Array<AlgoliaSearchSalesIncentive> | undefined;
    discountPercentage: number | null;
    promotions: Array<AlgoliaSearchPromotion>;
}): PromoLabel | undefined => {
    const bffSalesIncentives = salesIncentives?.map(
        SalesIncentiveMapper.fromAlgoliaSearchToBff
    );

    const bffPromotions = promotions?.map(
        PromotionMapper.fromAlgoliaSearchToBff
    );

    return getPromoLabel(
        bffSalesIncentives ?? [],
        discountPercentage,
        bffPromotions
    );
};

const fromBffSearchToBff = ({
    salesIncentives,
    discountPercentage,
    promotions
}: {
    salesIncentives: Array<BffSearchSalesIncentive> | undefined;
    discountPercentage: number | null;
    promotions: Array<BffSearchPromotion>;
}): PromoLabel | undefined => {
    const bffSalesIncentives = salesIncentives?.map(
        SalesIncentiveMapper.fromBffSearchToBff
    );

    const bffPromotions = promotions?.map(PromotionMapper.fromBffSearchToBff);

    return getPromoLabel(
        bffSalesIncentives ?? [],
        discountPercentage,
        bffPromotions
    );
};

export const PromoLabelMapper = {
    fromCloudToBff: fromCloudToBff,
    fromAlgoliaSearchToBff: fromAlgoliaSearchToBff,
    fromBffSearchToBff: fromBffSearchToBff
};
